<template>
  <div class="reviews-root">
    <!-- TOPBAR -->
    <div class="topbar d-flex align-items-center px-3 py-2">
      <button class="btn btn-sm btn-back me-2" @click="goBack">
        <i class="mdi mdi-arrow-left"></i>
      </button>
      <h6 class="mb-0 fw-bold text-gold flex-grow-1">
        {{ view === 'write' ? 'Write Review' : 'Reviews & Ratings' }}
      </h6>
      <button v-if="view === 'list'" class="btn btn-sm btn-outline-gold" @click="toggleTab">
        <i :class="'mdi mdi-' + (tab === 'received' ? 'star-outline' : 'star')"></i>
        {{ tab === 'received' ? 'Given' : 'Received' }}
      </button>
    </div>

    <!-- ==================== WRITE REVIEW ==================== -->
    <div v-if="view === 'write'" class="write-view px-3 pb-4">
      <!-- Order Context Card -->
      <div class="context-card mb-3">
        <div class="d-flex align-items-center">
          <div class="context-icon me-3">
            <i class="mdi mdi-package-variant-closed"></i>
          </div>
          <div class="flex-grow-1">
            <div class="text-muted-sm">Order #{{ orderCtx.orderNumber }}</div>
            <div class="fw-semibold text-light">{{ orderCtx.listingTitle }}</div>
            <div class="text-muted-sm">Reviewing: {{ orderCtx.sellerName }}</div>
          </div>
        </div>
      </div>

      <!-- Overall Rating -->
      <div class="rating-section mb-3">
        <label class="section-label">Overall Rating <span class="text-danger">*</span></label>
        <div class="star-row">
          <button
            v-for="s in 5" :key="'overall-'+s"
            class="star-btn"
            :class="{ active: form.overallRating >= s }"
            @click="form.overallRating = s"
          >
            <i :class="'mdi mdi-star' + (form.overallRating >= s ? '' : '-outline')"></i>
          </button>
          <span class="rating-label ms-2">{{ ratingLabel(form.overallRating) }}</span>
        </div>
      </div>

      <!-- Category Ratings -->
      <div class="rating-section mb-3">
        <label class="section-label">Detailed Ratings <span class="text-muted-sm">(optional)</span></label>
        <div class="category-grid">
          <div v-for="cat in categories" :key="cat.key" class="category-item">
            <div class="cat-label">
              <i :class="'mdi mdi-' + cat.icon"></i> {{ cat.label }}
            </div>
            <div class="cat-stars">
              <button
                v-for="s in 5" :key="cat.key+'-'+s"
                class="star-btn-sm"
                :class="{ active: form[cat.key] >= s }"
                @click="form[cat.key] = form[cat.key] === s ? 0 : s"
              >
                <i :class="'mdi mdi-star' + (form[cat.key] >= s ? '' : '-outline')"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Review Title -->
      <div class="mb-3">
        <label class="section-label">Review Title</label>
        <input
          v-model="form.reviewTitle"
          class="form-control input-dark"
          placeholder="Summarise your experience..."
          maxlength="100"
        />
      </div>

      <!-- Review Text -->
      <div class="mb-3">
        <label class="section-label">Your Review <span class="text-danger">*</span></label>
        <textarea
          v-model="form.reviewText"
          class="form-control input-dark"
          rows="4"
          placeholder="Tell others about your experience with this seller..."
          maxlength="1000"
        ></textarea>
        <div class="text-end text-muted-sm mt-1">{{ form.reviewText.length }}/1000</div>
      </div>

      <!-- Submit -->
      <button
        class="btn btn-gold w-100 py-2"
        :disabled="!canSubmit || submitting"
        @click="submitReview"
      >
        <span v-if="submitting"><i class="mdi mdi-loading mdi-spin me-1"></i> Submitting...</span>
        <span v-else><i class="mdi mdi-send me-1"></i> Submit Review</span>
      </button>

      <!-- Success overlay -->
      <div v-if="submitted" class="success-overlay">
        <div class="success-content">
          <i class="mdi mdi-check-circle success-icon"></i>
          <h5 class="text-gold mt-3 mb-1">Review Submitted!</h5>
          <p class="text-muted-sm mb-3">Thank you for your feedback</p>
          <button class="btn btn-gold px-4" @click="goBack">Done</button>
        </div>
      </div>
    </div>

    <!-- ==================== REVIEWS LIST ==================== -->
    <div v-if="view === 'list'" class="list-view px-3 pb-4">

      <!-- Stats Summary -->
      <div v-if="tab === 'received' && stats.length" class="stats-card mb-3">
        <div v-for="st in stats" :key="st.role_context" class="stat-block" :class="{ 'mt-3': stats.indexOf(st) > 0 }">
          <div class="d-flex align-items-center mb-2" v-if="stats.length > 1">
            <span class="badge badge-role me-2">{{ st.role_context }}</span>
          </div>
          <div class="d-flex align-items-start">
            <!-- Big number -->
            <div class="big-rating me-3 text-center">
              <div class="rating-number">{{ parseFloat(st.avg_overall || 0).toFixed(1) }}</div>
              <div class="star-display">
                <i v-for="s in 5" :key="'stat-'+s"
                  :class="'mdi mdi-star' + (s <= Math.round(parseFloat(st.avg_overall || 0)) ? '' : '-outline')"
                  class="star-sm"
                ></i>
              </div>
              <div class="text-muted-sm">{{ st.total_reviews || 0 }} reviews</div>
            </div>
            <!-- Bar breakdown -->
            <div class="flex-grow-1">
              <div v-for="bar in starBars(st)" :key="bar.label" class="bar-row d-flex align-items-center mb-1">
                <span class="bar-label">{{ bar.label }}</span>
                <div class="bar-track flex-grow-1 mx-2">
                  <div class="bar-fill" :style="{ width: bar.pct + '%' }"></div>
                </div>
                <span class="bar-count">{{ bar.count }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="text-center py-5">
        <i class="mdi mdi-loading mdi-spin text-gold" style="font-size:2rem"></i>
      </div>

      <!-- Empty -->
      <div v-else-if="!reviews.length" class="empty-state text-center py-5">
        <i class="mdi mdi-star-off-outline empty-icon"></i>
        <h6 class="text-light mt-3">No reviews yet</h6>
        <p class="text-muted-sm">{{ tab === 'received' ? 'Reviews from buyers will appear here' : 'Your reviews for sellers will appear here' }}</p>
      </div>

      <!-- Reviews -->
      <div v-else>
        <div v-for="r in reviews" :key="r.id" class="review-card mb-2" @click="toggleExpand(r.id)">
          <div class="d-flex align-items-start">
            <div class="reviewer-avatar me-2">
              <img v-if="r.reviewer_avatar" :src="r.reviewer_avatar" class="avatar-img" />
              <i v-else class="mdi mdi-account-circle avatar-placeholder"></i>
            </div>
            <div class="flex-grow-1 min-w-0">
              <div class="d-flex align-items-center justify-content-between">
                <span class="fw-semibold text-light text-truncate">{{ r.reviewer_name || 'User' }}</span>
                <span class="text-muted-sm ms-2 flex-shrink-0">{{ timeAgo(r.created_at) }}</span>
              </div>
              <!-- Stars -->
              <div class="d-flex align-items-center mt-1">
                <i v-for="s in 5" :key="'r-'+r.id+'-'+s"
                  :class="'mdi mdi-star' + (s <= Math.round(parseFloat(r.overall_rating)) ? '' : '-outline')"
                  class="star-sm"
                ></i>
                <span class="ms-1 text-gold fw-semibold" style="font-size:0.8rem">{{ parseFloat(r.overall_rating).toFixed(1) }}</span>
                <span v-if="r.is_verified_purchase == 1" class="badge badge-verified ms-2">
                  <i class="mdi mdi-check-decagram"></i> Verified
                </span>
              </div>
              <!-- Title -->
              <div v-if="r.review_title" class="fw-semibold text-light mt-1" style="font-size:0.9rem">{{ r.review_title }}</div>
              <!-- Text -->
              <div class="review-text mt-1" :class="{ expanded: expandedId === r.id }">{{ r.review_text }}</div>

              <!-- Category ratings (expanded) -->
              <div v-if="expandedId === r.id && hasCategoryRatings(r)" class="category-breakdown mt-2">
                <div v-if="r.quality_rating" class="cat-row">
                  <span class="cat-name"><i class="mdi mdi-diamond-stone"></i> Quality</span>
                  <span class="cat-val">{{ r.quality_rating }}/5</span>
                </div>
                <div v-if="r.communication_rating" class="cat-row">
                  <span class="cat-name"><i class="mdi mdi-message-text"></i> Communication</span>
                  <span class="cat-val">{{ r.communication_rating }}/5</span>
                </div>
                <div v-if="r.timeliness_rating" class="cat-row">
                  <span class="cat-name"><i class="mdi mdi-clock-outline"></i> Timeliness</span>
                  <span class="cat-val">{{ r.timeliness_rating }}/5</span>
                </div>
              </div>

              <!-- Seller Response -->
              <div v-if="r.response_text && expandedId === r.id" class="seller-response mt-2">
                <div class="d-flex align-items-center mb-1">
                  <i class="mdi mdi-reply me-1 text-gold"></i>
                  <span class="fw-semibold text-gold" style="font-size:0.8rem">Seller Response</span>
                </div>
                <div class="response-text">{{ r.response_text }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { execute, flocker } from "@/api";

export default {
  name: "ReviewsPage",
  data() {
    return {
      darkMode:(document.documentElement.getAttribute('data-theme')||localStorage.getItem('flockr_theme')||'dark')!=='light',

      view: "list", // list | write
      tab: "received", // received | given
      loading: false,
      submitting: false,
      submitted: false,
      expandedId: null,
      reviews: [],
      stats: [],
      orderCtx: {
        orderId: "",
        orderNumber: "",
        listingTitle: "",
        sellerId: "",
        sellerName: "",
        contextType: "animal_order",
      },
      form: {
        overallRating: 0,
        qualityRating: 0,
        communicationRating: 0,
        timelinessRating: 0,
        reviewTitle: "",
        reviewText: "",
      },
      categories: [
        { key: "qualityRating", label: "Quality", icon: "diamond-stone" },
        { key: "communicationRating", label: "Communication", icon: "message-text" },
        { key: "timelinessRating", label: "Timeliness", icon: "clock-outline" },
      ],
    };
  },
  computed: {
    themePageStyle(){return this.darkMode?'background:#0A0A0A;min-height:100vh;color:#F5F0E8;':'background:#F7F5F2;min-height:100vh;color:#1A1A1A;'},
    themeCardStyle(){return this.darkMode?'background:#141414!important;border:1px solid #1E1E1E!important;':'background:#FFFFFF!important;border:1px solid #E8E2DA!important;'},
    themeInputStyle(){return this.darkMode?'background:#0F0F0F;border:1px solid #2A2520;color:#F5F0E8;':'background:#FFFFFF;border:1px solid #E0D8D0;color:#1A1A1A;'},

    canSubmit() {
      return this.form.overallRating > 0 && this.form.reviewText.trim().length >= 10;
    },
  },
  methods: {
    goBack() {
      if (this.view === "write" && this.orderCtx.orderId) {
        this.$router.push("/orders/" + this.orderCtx.orderId);
      } else {
        this.$router.push("/orders");
      }
    },
    toggleTab() {
      this.tab = this.tab === "received" ? "given" : "received";
      this.loadReviews();
    },
    toggleExpand(id) {
      this.expandedId = this.expandedId === id ? null : id;
    },
    ratingLabel(v) {
      if (v === 0) return "";
      const labels = ["", "Poor", "Fair", "Good", "Very Good", "Excellent"];
      return labels[v] || "";
    },
    starBars(st) {
      const total = parseInt(st.total_reviews) || 1;
      return [
        { label: "5★", count: parseInt(st.five_star_count) || 0, pct: ((parseInt(st.five_star_count) || 0) / total) * 100 },
        { label: "4★", count: parseInt(st.four_star_count) || 0, pct: ((parseInt(st.four_star_count) || 0) / total) * 100 },
        { label: "3★", count: parseInt(st.three_star_count) || 0, pct: ((parseInt(st.three_star_count) || 0) / total) * 100 },
        { label: "2★", count: parseInt(st.two_star_count) || 0, pct: ((parseInt(st.two_star_count) || 0) / total) * 100 },
        { label: "1★", count: parseInt(st.one_star_count) || 0, pct: ((parseInt(st.one_star_count) || 0) / total) * 100 },
      ];
    },
    hasCategoryRatings(r) {
      return r.quality_rating || r.communication_rating || r.timeliness_rating;
    },
    timeAgo(dt) {
      if (!dt) return "";
      const now = new Date();
      const d = new Date(dt);
      const diffMs = now - d;
      const mins = Math.floor(diffMs / 60000);
      if (mins < 1) return "Just now";
      if (mins < 60) return mins + "m ago";
      const hrs = Math.floor(mins / 60);
      if (hrs < 24) return hrs + "h ago";
      const days = Math.floor(hrs / 24);
      if (days < 7) return days + "d ago";
      if (days < 30) return Math.floor(days / 7) + "w ago";
      return d.toLocaleDateString("en-KE", { day: "numeric", month: "short", year: "numeric" });
    },

    async loadReviews() {
      this.loading = true;
      this.reviews = [];
      this.stats = [];
      try {
        const fd = new FormData();
        fd.append("function", "getUserReviews");
        fd.append("userId", sessionStorage.getItem('userId'));
        const res = await execute(fd, flocker);
        if (res && res.data && res.data.success) {
          const data = res.data.data || {};
          const allReviews = data.reviews || [];
          // Filter by tab
          if (this.tab === "received") {
            this.reviews = allReviews.filter((r) => r.reviewed_id == sessionStorage.getItem('userId'));
          } else {
            this.reviews = allReviews.filter((r) => r.reviewer_id == sessionStorage.getItem('userId'));
          }
          this.stats = Array.isArray(data.stats) ? data.stats : data.stats ? [data.stats] : [];
        }
      } catch (e) {
        console.error("loadReviews error:", e);
      }
      this.loading = false;
    },

    async submitReview() {
      if (!this.canSubmit || this.submitting) return;
      this.submitting = true;
      try {
        const fd = new FormData();
        fd.append("function", "submitRating");
        fd.append("reviewerId", sessionStorage.getItem('userId'));
        fd.append("reviewedId", this.orderCtx.sellerId);
        fd.append("contextType", this.orderCtx.contextType);
        fd.append("contextId", this.orderCtx.orderId);
        fd.append("overallRating", this.form.overallRating);
        if (this.form.qualityRating) fd.append("qualityRating", this.form.qualityRating);
        if (this.form.communicationRating) fd.append("communicationRating", this.form.communicationRating);
        if (this.form.timelinessRating) fd.append("timelinessRating", this.form.timelinessRating);
        fd.append("reviewTitle", this.form.reviewTitle);
        fd.append("reviewText", this.form.reviewText);
        const res = await execute(fd, flocker);
        if (res && res.data && res.data.success) {
          this.submitted = true;
        } else {
          alert((res?.data?.message) || "Failed to submit review");
        }
      } catch (e) {
        console.error("submitReview error:", e);
        alert("Network error");
      }
      this.submitting = false;
    },
  },
  mounted() {
    // Apply global theme
    var t=localStorage.getItem('flockr_theme')||'dark';window.__flockrSetTheme(t);

    const q = this.$route.query;
    if (q.orderId && q.sellerId) {
      // Write mode from OrderDetail
      this.view = "write";
      this.orderCtx.orderId = q.orderId;
      this.orderCtx.orderNumber = q.orderNumber || "";
      this.orderCtx.listingTitle = q.listingTitle || "";
      this.orderCtx.sellerId = q.sellerId;
      this.orderCtx.sellerName = q.sellerName || "";
      this.orderCtx.contextType = q.contextType || "animal_order";
    } else {
      // List mode
      this.view = "list";
      this.loadReviews();
    }
  },
};
</script>

<style>

/* === GLOBAL THEME === */
html[data-theme="light"] body { background:#FFFFFF!important; color:#1A1A1A!important; }
html[data-theme="light"] .fm-card,html[data-theme="light"] .p2c,html[data-theme="light"] .ld-section,html[data-theme="light"] .ld-action-card,html[data-theme="light"] .ld-seller-card,html[data-theme="light"] .ld-info-card,html[data-theme="light"] .ld-related-card { background:#FFFFFF!important; border-color:#E8E2DA!important; }
html[data-theme="light"] .fm-overlay .fm-modal,html[data-theme="light"] .ad-overlay .ad-modal { background:#FFFFFF!important; border-color:#E8E2DA!important; }
html[data-theme="light"] .fm-input,html[data-theme="light"] .fm-select { background:#F7F5F2!important; border-color:#E0D8D0!important; color:#1A1A1A!important; }
html[data-theme="light"] .fm-label { color:#555!important; }
html[data-theme="dark"] body { background:#0A0A0A!important; color:#F5F0E8!important; }
</style>
<style>
:root{--fm-h:#F5F0E8;--fm-s:#6B6356;--fm-m:#A09888;--fm-bg1:#141414;--fm-bg2:#0F0F0F;--fm-bg3:#1E1E1E;--fm-br:#1E1E1E}
html[data-theme="light"]{--fm-h:#1A1A1A;--fm-s:#777;--fm-m:#555;--fm-bg1:#FFFFFF;--fm-bg2:#F7F5F2;--fm-bg3:#F0EBE4;--fm-br:#E8E2DA}
html[data-theme="light"] body{background:#F7F5F2!important;color:#1A1A1A!important}
html[data-theme="light"] .card{background:#FFFFFF!important;border-color:#E8E2DA!important}
html[data-theme="light"] .card-body{color:#1A1A1A!important}
html[data-theme="light"] .modal-content{background:#FFFFFF!important;border-color:#E8E2DA!important;color:#1A1A1A!important}
html[data-theme="light"] .form-control,html[data-theme="light"] .form-select{background:#FFFFFF!important;border-color:#E0D8D0!important;color:#1A1A1A!important}
html[data-theme="light"] .table{color:#1A1A1A!important}
html[data-theme="light"] .table th{color:#555!important;border-color:#E8E2DA!important}
html[data-theme="light"] .table td{border-color:#F0EBE4!important}
html[data-theme="light"] [style*="color:#F5F0E8"]{color:#1A1A1A!important}
html[data-theme="light"] [style*="color:#6B6356"]{color:#777!important}
html[data-theme="light"] [style*="color:#A09888"]{color:#555!important}
html[data-theme="light"] [style*="background:#0F0F0F"]{background:#F7F5F2!important}
html[data-theme="light"] [style*="background:#141414"]{background:#FFFFFF!important}
html[data-theme="light"] [style*="background:#1E1E1E"]{background:#F0EBE4!important}
html[data-theme="light"] [style*="background:#0A0A0A"]{background:#F7F5F2!important}
html[data-theme="light"] [style*="background:#1a1510"]{background:#FFF8F0!important}
html[data-theme="light"] [style*="background:#1A1A1A"]{background:#F0EBE4!important}
html[data-theme="light"] [style*="border:1px solid #1E1E1E"]{border-color:#E8E2DA!important}
html[data-theme="light"] [style*="border:1px solid #2A2520"]{border-color:#E0D8D0!important}
html[data-theme="light"] [style*="border-bottom:1px solid #1A1A1A"]{border-bottom-color:#F0EBE4!important}
html[data-theme="light"] [style*="border-bottom:1px solid #1E1E1E"]{border-bottom-color:#F0EBE4!important}
html[data-theme="light"] [style*="border-bottom:1px solid #2A2520"]{border-bottom-color:#E0D8D0!important}
html[data-theme="light"] [style*="border-top:1px solid #2A2520"]{border-top-color:#E0D8D0!important}
html[data-theme="light"] [style*="color:#BFA164"]{color:#BFA164!important}
html[data-theme="light"] [style*="color:#50C878"]{color:#50C878!important}
html[data-theme="light"] [style*="color:#4FC3F7"]{color:#4FC3F7!important}
html[data-theme="light"] [style*="color:#FF6B6B"]{color:#FF6B6B!important}
html[data-theme="light"] [style*="color:#FF9800"]{color:#FF9800!important}
html[data-theme="light"] [style*="color:#CE93D8"]{color:#CE93D8!important}
</style>
<style scoped>
.reviews-root {
  background: #0A0A0A;
  min-height: 100vh;
  color: #F5F0E8;
  position: relative;
}

/* TOPBAR */
.topbar {
  background: #111;
  border-bottom: 1px solid #1a1a1a;
  position: sticky;
  top: 0;
  z-index: 10;
}
.text-gold { color: #BFA164 !important; }
.btn-back {
  background: transparent;
  border: 1px solid #333;
  color: #F5F0E8;
  border-radius: 8px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}
.btn-back:hover { border-color: #BFA164; color: #BFA164; }
.btn-outline-gold {
  border: 1px solid #BFA164;
  color: #BFA164;
  background: transparent;
  border-radius: 20px;
  font-size: 0.78rem;
  padding: 3px 12px;
}
.btn-outline-gold:hover { background: rgba(191,161,100,0.1); color: #BFA164; }
.btn-gold {
  background: linear-gradient(135deg, #BFA164, #D4B977);
  color: #0A0A0A;
  border: none;
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.95rem;
}
.btn-gold:disabled { opacity: 0.4; }
.btn-gold:not(:disabled):hover { background: linear-gradient(135deg, #D4B977, #BFA164); }

/* HELPERS */
.text-muted-sm { color: #777; font-size: 0.78rem; }

/* CONTEXT CARD */
.context-card {
  background: #141414;
  border: 1px solid #1e1e1e;
  border-radius: 12px;
  padding: 14px;
  margin-top: 12px;
}
.context-icon {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: rgba(191,161,100,0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.3rem;
  color: #BFA164;
}

/* STAR RATINGS */
.section-label {
  display: block;
  font-size: 0.85rem;
  font-weight: 600;
  color: #ccc;
  margin-bottom: 8px;
}
.star-row {
  display: flex;
  align-items: center;
}
.star-btn {
  background: none;
  border: none;
  padding: 2px 4px;
  font-size: 2rem;
  color: #333;
  cursor: pointer;
  transition: color 0.15s, transform 0.15s;
}
.star-btn.active { color: #BFA164; }
.star-btn:hover { transform: scale(1.15); }
.rating-label {
  font-size: 0.85rem;
  color: #BFA164;
  font-weight: 600;
}

/* CATEGORY RATINGS */
.category-grid {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.category-item {
  background: #141414;
  border: 1px solid #1e1e1e;
  border-radius: 10px;
  padding: 10px 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.cat-label {
  font-size: 0.82rem;
  color: #ccc;
}
.cat-label i { color: #BFA164; margin-right: 4px; }
.cat-stars { display: flex; gap: 2px; }
.star-btn-sm {
  background: none;
  border: none;
  padding: 0 2px;
  font-size: 1.2rem;
  color: #333;
  cursor: pointer;
  transition: color 0.15s;
}
.star-btn-sm.active { color: #BFA164; }

/* INPUT */
.input-dark {
  background: #141414 !important;
  border: 1px solid #1e1e1e !important;
  color: #F5F0E8 !important;
  border-radius: 10px !important;
  font-size: 0.9rem;
}
.input-dark::placeholder { color: #555; }
.input-dark:focus { border-color: #BFA164 !important; box-shadow: 0 0 0 2px rgba(191,161,100,0.15) !important; }

/* SUCCESS OVERLAY */
.success-overlay {
  position: fixed;
  inset: 0;
  background: rgba(10,10,10,0.95);
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
}
.success-content { text-align: center; }
.success-icon { font-size: 4rem; color: #4CAF50; }

/* STATS CARD */
.stats-card {
  background: #141414;
  border: 1px solid #1e1e1e;
  border-radius: 12px;
  padding: 16px;
  margin-top: 12px;
}
.big-rating { min-width: 70px; }
.rating-number {
  font-size: 2.2rem;
  font-weight: 700;
  color: #BFA164;
  line-height: 1;
}
.star-display { margin: 4px 0 2px; }
.star-sm { font-size: 0.85rem; color: #BFA164; }
.badge-role {
  background: rgba(191,161,100,0.15);
  color: #BFA164;
  font-size: 0.7rem;
  padding: 2px 8px;
  border-radius: 10px;
  text-transform: capitalize;
}

/* BAR CHART */
.bar-row { height: 20px; }
.bar-label { font-size: 0.72rem; color: #888; width: 24px; text-align: right; }
.bar-track {
  height: 6px;
  background: #1e1e1e;
  border-radius: 3px;
  overflow: hidden;
}
.bar-fill {
  height: 100%;
  background: linear-gradient(90deg, #BFA164, #D4B977);
  border-radius: 3px;
  transition: width 0.5s ease;
}
.bar-count { font-size: 0.72rem; color: #888; width: 20px; }

/* REVIEW CARD */
.review-card {
  background: #141414;
  border: 1px solid #1e1e1e;
  border-radius: 12px;
  padding: 12px 14px;
  cursor: pointer;
  transition: border-color 0.2s;
}
.review-card:hover { border-color: #2a2a2a; }
.reviewer-avatar { flex-shrink: 0; }
.avatar-img {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  object-fit: cover;
}
.avatar-placeholder { font-size: 2.2rem; color: #444; }
.min-w-0 { min-width: 0; }
.badge-verified {
  background: rgba(76,175,80,0.15);
  color: #4CAF50;
  font-size: 0.68rem;
  padding: 1px 6px;
  border-radius: 8px;
}
.review-text {
  font-size: 0.85rem;
  color: #bbb;
  line-height: 1.45;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.review-text.expanded {
  -webkit-line-clamp: unset;
  overflow: visible;
}

/* CATEGORY BREAKDOWN */
.category-breakdown {
  background: #0e0e0e;
  border-radius: 8px;
  padding: 8px 10px;
}
.cat-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 3px 0;
  font-size: 0.78rem;
}
.cat-name { color: #999; }
.cat-name i { color: #BFA164; font-size: 0.85rem; margin-right: 4px; }
.cat-val { color: #BFA164; font-weight: 600; }

/* SELLER RESPONSE */
.seller-response {
  background: rgba(191,161,100,0.06);
  border-left: 2px solid #BFA164;
  border-radius: 0 8px 8px 0;
  padding: 8px 10px;
}
.response-text { font-size: 0.82rem; color: #bbb; line-height: 1.4; }

/* EMPTY */
.empty-icon { font-size: 3.5rem; color: #333; }
</style>
